import type { GetServerSidePropsContext } from 'next';
import type { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies';
import type { DestroyCookie, SetCookie } from '../../providers/CookieProvider/CookieProvider';

import { buildTestCookies } from './buildTestCookies/buildTestCookies';
import { getParticipantCookieName, getTestCookieName } from './cookieNames/cookieNames';

type SetTestCookiesParameters = {
  context?: GetServerSidePropsContext;
  testId: string;
  setCookie: SetCookie;
  value: {
    participant: string;
    variant: Record<string, string>;
  };
};

export const setTestCookies = ({ context, testId, setCookie, value }: SetTestCookiesParameters) => {
  const testCookies = buildTestCookies({ testId, ruleset: value });

  testCookies.forEach((cookie) => {
    setCookie(context, cookie.name, cookie.value, {
      expires: cookie.expires,
      path: cookie.path,
    });
  });
};

type RemoveTestCookieParameters = {
  context?: GetServerSidePropsContext;
  testId: string;
  removeCookie: DestroyCookie;
};

export const removeTestCookies = ({
  context,
  testId,
  removeCookie,
}: RemoveTestCookieParameters) => {
  removeCookie(context, getParticipantCookieName(testId), {
    path: '/',
  });
  removeCookie(context, getTestCookieName(testId), {
    path: '/',
  });
};

const getParticipation = (fullValue: string | undefined, participantId: string | undefined) => {
  if (fullValue) {
    const { participant, variant } = JSON.parse(fullValue) as {
      participant: string;
      variant: Record<string, string>;
    };

    return { participantId: participant, variant };
  }

  if (participantId) {
    return { participantId, variant: undefined };
  }

  return undefined;
};

export const getTestParticipation = (
  cookies: Record<string, string>,
  testId: string,
):
  | {
      participantId: string;
      variant?: Record<string, string>;
    }
  | undefined => {
  const fullCookieName = getTestCookieName(testId);
  const participantCookieName = getParticipantCookieName(testId);

  return getParticipation(cookies[fullCookieName], cookies[participantCookieName]);
};

export const getTestParticipationFromCookieStore = async (
  testId: string,
  cookieStore: ReadonlyRequestCookies,
) => {
  const fullCookieName = getTestCookieName(testId);
  const participantCookieName = getParticipantCookieName(testId);

  const fullValue = await cookieStore.get(fullCookieName);
  const participantId = await cookieStore.get(participantCookieName);

  return getParticipation(fullValue?.value, participantId?.value);
};

export const isAbTestParticipant = ({
  testCookie,
  customTestRecognizerFunction,
}: {
  testCookie: string;
  customTestRecognizerFunction?: () => boolean;
}) => {
  if (testCookie) {
    return !!testCookie;
  }
  if (customTestRecognizerFunction) {
    return customTestRecognizerFunction();
  }
  return false;
};
